import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, DatabaseBackup, Rocket } from "lucide-react";

export function DataTransfer({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);
  const inputRef = useRef(null);

  const exportAll = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pomata-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exportado: ${res.data.tracks.length} tracks, ${res.data.packs.length} packs`);
    } catch {
      toast.error("No se pudo exportar");
    } finally { setBusy(false); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = JSON.parse(await file.text());
      if (!data.tracks && !data.packs && !data.site_config) throw new Error("bad");
      const res = await api.post(`/admin/import?replace=${replace}`, data);
      toast.success(`Importado: ${res.data.tracks} tracks, ${res.data.packs} packs${res.data.config ? ", config" : ""}`);
      onDone();
    } catch {
      toast.error("Archivo no válido o error al importar");
    } finally { setBusy(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncProd = async () => {
    if (!window.confirm("Se publicará TODO el catálogo de esta web (tracks, packs, códigos y configuración) en pomatainsession.com, reemplazando el que haya allí. ¿Continuar?")) return;
    setSyncing(true);
    try {
      const res = await api.post("/admin/sync-to-production");
      toast.success(`Publicado en ${res.data.target}: ${res.data.tracks} tracks · ${res.data.packs} packs · ${res.data.access_codes} códigos`);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo publicar en producción"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="max-w-2xl bg-[#111116] border border-white/5 rounded-2xl p-6 space-y-4" data-testid="data-transfer">
      <div className="p-4 rounded-xl border border-red-900/40 bg-red-950/10 space-y-2" data-testid="sync-production">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Rocket className="w-5 h-5 text-red-500" /> Publicar en producción</h3>
        <p className="text-xs text-white/50 leading-relaxed">Envía en un clic todo lo que has subido y editado aquí a <b className="text-white/80">pomatainsession.com</b> (tracks, packs, códigos VIP y configuración). El catálogo de producción se reemplaza por este.</p>
        <button type="button" onClick={syncProd} disabled={syncing || busy} data-testid="sync-production-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full btn-red text-sm font-semibold text-white disabled:opacity-50">
          <Rocket className="w-4 h-4" /> {syncing ? "Publicando..." : "Publicar en pomatainsession.com"}
        </button>
      </div>
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><DatabaseBackup className="w-5 h-5 text-red-500" /> Copiar datos entre webs</h3>
      <p className="text-xs text-white/50 leading-relaxed">
        Exporta tracks, packs y configuración (logo, banner, PayPal…) desde una web e impórtalos en otra. Los archivos de audio e imágenes se comparten, solo se copian los datos.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={exportAll} disabled={busy} data-testid="export-data-button"
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-red-600/60 hover:text-red-500 text-sm font-semibold text-white/80 disabled:opacity-50">
          <Download className="w-4 h-4" /> Exportar datos
        </button>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="import-data-button"
          className="flex items-center gap-2 px-4 py-2 rounded-full btn-red text-sm font-semibold text-white disabled:opacity-50">
          <Upload className="w-4 h-4" /> {busy ? "Procesando..." : "Importar datos"}
        </button>
        <input ref={inputRef} type="file" accept="application/json,.json" className="hidden" data-testid="import-data-input"
          onChange={(e) => { importFile(e.target.files[0]); e.target.value = ""; }} />
        <label className="flex items-center gap-2 text-xs text-white/60">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="import-replace-checkbox" />
          Borrar tracks/packs actuales antes de importar
        </label>
      </div>
    </div>
  );
}
